import request, {
  convertData,
  MutationOptions,
  QueryVariables,
  useMakeMutation,
} from '@shared/innmaxLib/services'
import React from 'react'
import { useQuery, UseQueryOptions } from 'react-query'
import { useConvertOtaFile, useConvertOtaRecord } from './ota.converter'
import { IOtaFile, IOtaRecord } from './ota.type'

export const useOTARecordList = (query?: any, options?: UseQueryOptions) => {
  const { converter } = useConvertOtaRecord()

  const { data, ...others } = useQuery(
    ['/ota/task/{pageNum}/{pageSize}', query],
    {
      ...options,
    }
  )

  const dataSource = React.useMemo(() => {
    return convertData<IOtaRecord>(
      {
        converter,
      },
      data
    )
  }, [data, converter])

  return {
    ...others,
    dataSource,
    loading: others.isLoading,
  }
}

export const useOTARecordExport = (query?: any, options?: QueryVariables) => {
  const [enabled, setIsEnabled] = React.useState(false)

  const { isLoading } = useQuery(['/ota/task/export', { ...query }], {
    ...options,
    enabled,
    onSuccess: (response: any) => {
      if (options?.onSuccess) {
        options.onSuccess(response)
      }
      setIsEnabled(false)
    },
    onError: error => {
      console.log(error)
      setIsEnabled(false)
    },
  })

  return [
    isLoading,
    () => {
      setIsEnabled(true)
    },
  ] as [boolean, () => void]
}

export const useOTARecordById = (id?: string, options?: UseQueryOptions) => {
  const { converter } = useConvertOtaRecord()

  const { data: _data, ...others } = useQuery(['/ota/task/{id}', { id }], {
    enabled: !!id,
    ...options,
  })

  const data = React.useMemo(() => {
    return converter((_data as any)?.data)
  }, [_data, converter])

  return {
    ...others,
    data,
    loading: others.isLoading,
  }
}

export const useAddOTARecord = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/ota/task', {
        method: 'POST',
        body: values,
      }),
    options
  )
}

export const useEditOTARecord = (options: MutationOptions = {}) => {
  return useMakeMutation(
    ({ id, ...values }: Partial<any> = {}) =>
      request(`/ota/task/${id}`, {
        method: 'PUT',
        body: values,
      }),
    options
  )
}

export const useDeleteOTARecord = (options: MutationOptions = {}) => {
  return useMakeMutation(
    ({ id }: { id: string }) =>
      request(`/ota/task/${id}`, {
        method: 'DELETE',
      }),
    options
  )
}

export const useOTAFileList = (query?: any, options?: UseQueryOptions) => {
  const { converter } = useConvertOtaFile()

  const { data, ...others } = useQuery(
    ['/ota/firmware/{pageNum}/{pageSize}', query],
    {
      ...options,
    }
  )

  const dataSource = React.useMemo(() => {
    return convertData<IOtaFile>(
      {
        converter,
      },
      data
    )
  }, [data, converter])

  return {
    ...others,
    dataSource,
    loading: others.isLoading,
  }
}

export const useOTAFileById = (id?: string, options?: UseQueryOptions) => {
  const { converter } = useConvertOtaFile()

  const { data: _data, ...others } = useQuery(['/ota/firmware/{id}', { id }], {
    enabled: !!id,
    ...options,
  })

  const data = React.useMemo(() => {
    return converter((_data as any)?.data)
  }, [_data, converter])

  return {
    ...others,
    data,
    loading: others.isLoading,
  }
}

export const useAddOTAFirmware = (options: MutationOptions = {}) => {
  return useMakeMutation(
    (values: Partial<any> = {}) =>
      request('/ota/firmware', {
        method: 'POST',
        body: values,
      }),
    options
  )
}

export const useEditOTAFirmware = (options: MutationOptions = {}) => {
  return useMakeMutation(
    ({ id, ...values }: Partial<any> = {}) =>
      request(`/ota/firmware/${id}`, {
        method: 'PUT',
        body: values,
      }),
    options
  )
}

export const useDeleteOTAFirmware = (options: MutationOptions = {}) => {
  return useMakeMutation(
    ({ id }: { id: string }) =>
      request(`/ota/firmware/${id}`, {
        method: 'DELETE',
      }),
    options
  )
}
